import { DatePicker, TimePicker } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFormattedUserTimeZone } from '../../../common/utils/utils';
import { useToken } from '@/common/helper';
import { useEffect } from 'react';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

interface SchedulePickerProps {
  selectedDate: Dayjs | null;
  setSelectedDate: (date: Dayjs | null) => void;
  selectedTime: Dayjs | null;
  setSelectedTime: (time: Dayjs | null) => void;
}
/**
 * SchedulePicker Component
 *
 * Provides date and time selection with timezone display and validation.
 * Prevents selection of past dates and times, with disabled hours/minutes
 * for today based on current time.
 */
const SchedulePicker = ({
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
}: SchedulePickerProps) => {
  const { decodedUserToken } = useToken();
  const dateFormat = decodedUserToken()?.date_format;
  const dayPickerFormat =
    dateFormat === 'DD/MM/YYYY'
      ? 'ddd, D MMM'
      : dateFormat === 'YYYY/MM/DD'
        ? 'ddd, YYYY MMM D'
        : 'ddd, MMM D';
  function resolveEffectiveTimezone(): string {
    const isTimeZone = decodedUserToken()?.time_zone_enable;
    if (isTimeZone) {
      const tzString = getFormattedUserTimeZone();
      const match = tzString.match(/ ([A-Za-z_/-]+)$/);
      return match ? match[1] : dayjs.tz.guess();
    }
    return dayjs.tz.guess();
  }

  const userTz = resolveEffectiveTimezone();

  const now = dayjs.tz(dayjs(), userTz);
  useEffect(() => {
    if (!selectedTime) {
      setSelectedTime(now); // set current time in user's timezone
    }
  }, [userTz, selectedTime, setSelectedTime, now]);

  const isToday =
    !!selectedDate &&
    dayjs.tz(selectedDate.toDate(), userTz).isSame(now, 'day');

  const disabledDate = (current?: Dayjs): boolean => {
    if (!current) return false;
    const currentInTz = dayjs.tz(current.toDate(), userTz);
    return currentInTz.isBefore(now.startOf('day'));
  };

  const disabledHours = (): number[] => {
    if (!isToday) return [];
    const hours: number[] = [];
    for (let i = 0; i < now.hour(); i++) hours.push(i);
    return hours;
  };
  const disabledMinutes = (selectedHour: number): number[] => {
    if (!isToday || selectedHour > now.hour()) return [];
    const minutes: number[] = [];
    for (let i = 0; i < now.minute(); i++) minutes.push(i);
    return minutes;
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start',
          marginBottom: '8px',
        }}
      >
        <div style={{ flex: 1 }}>
          <label
            style={{ fontWeight: 600, marginBottom: '4px', display: 'block' }}
          >
            Day
          </label>
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            format={dayPickerFormat}
            className='custom-picker'
            style={{
              width: '100%',
              backgroundColor: 'var(--light-gray)',
              borderRadius: '8px',
              border: 'none',
              height: '36px',
              padding: '8px 12px',
            }}
            suffixIcon={<DownOutlined style={{ fontSize: 12 }} />}
            popupStyle={{ borderRadius: '12px' }}
            disabledDate={disabledDate}
          />
        </div>

        <div style={{ flex: 1 }}>
          <label
            style={{ fontWeight: 600, marginBottom: '4px', display: 'block' }}
          >
            Time
          </label>
          <TimePicker
            value={selectedTime}
            onChange={setSelectedTime}
            format='h:mm a'
            className='custom-picker'
            style={{
              width: '100%',
              backgroundColor: 'var(--light-gray)',
              borderRadius: '8px',
              border: 'none',
              height: '36px',
              padding: '8px 12px',
            }}
            use12Hours
            popupStyle={{ borderRadius: '12px' }}
            suffixIcon={null}
            disabledHours={disabledHours}
            disabledMinutes={disabledMinutes}
          />
          <p style={{ color: '#777', fontSize: '12px', marginTop: '4px' }}>
            {getFormattedUserTimeZone()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SchedulePicker;
